/**
 * A NYILAK A HASÁBOK KÖZÖTT (#68, Viktor 2026-10-20).
 *
 * `→` jobbra lép, `←` balra: kupac → papír → jelöltek („Melyik hiányt zárja
 * le?") → Tételsorok. A kupac ↔ papír lépést a munkalista rövidítése és a
 * néző maga intézi; itt a bizonylat régióján BELÜLI lépések vannak.
 *
 * ⚠️ A SZÉLSZABÁLY MINDENHOL UGYANAZ: a nyíl csak akkor hagyja el a helyét,
 * ha ott már nincs mit mozgatnia. A papíron görget, amíg nem ért a szélére
 * (`DocumentViewer`); a jegyzetben a kurzort viszi, amíg nem a szöveg
 * elején áll. A jelöltek rádiógombjain a vízszintes nyíl magától a
 * választást váltaná — azt itt elvesszük: a választás a `Space`-é és az
 * egéré, a vízszintes nyíl a hasábé, a függőleges a hasábon belüli lépés
 * (`stepColumn`).
 */

/** A kijelölt jelölt rádiógombja, vagy az első — `false`, ha nincs jelölt. */
export function focusCandidates(region: HTMLElement | null): boolean {
  const radios = [
    ...(region?.querySelectorAll<HTMLInputElement>('.worklist__candidates input[type="radio"]') ??
      []),
  ];
  const target = radios.find((radio) => radio.checked) ?? radios[0];
  target?.focus();
  return target !== undefined;
}

/** Az első jegyzet mezője — `false`, ha nincs (a puska még tölt). */
export function focusNotes(region: HTMLElement | null): boolean {
  const field = region?.querySelector<HTMLTextAreaElement>('.booking__note');
  field?.focus();
  return field !== undefined && field !== null;
}

function isCandidateRadio(target: EventTarget): target is HTMLInputElement {
  return (
    target instanceof HTMLInputElement &&
    target.type === 'radio' &&
    target.closest('.worklist__candidates') !== null
  );
}

function isNoteAtStart(target: EventTarget): boolean {
  return (
    target instanceof HTMLTextAreaElement &&
    target.classList.contains('booking__note') &&
    target.selectionStart === 0 &&
    target.selectionEnd === 0
  );
}

/**
 * A régió billentyűi. `true`, ha lekezelte — akkor a hívó elnyeli, hogy se a
 * rádió saját nyila, se a munkalista ablak-szintű `→`-je ne fusson le.
 * A hasábon belüli `↑`/`↓` is itt van (`stepColumn`).
 */
export function columnKey(
  event: {
    key: string;
    target: EventTarget;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
    shiftKey: boolean;
  },
  region: HTMLElement | null,
  paper: HTMLElement | null
): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
    return false;
  }
  if (isCandidateRadio(event.target)) {
    if (event.key === 'ArrowRight') {
      focusNotes(region);
      return true;
    }
    if (event.key === 'ArrowLeft') {
      paper?.focus();
      return true;
    }
  }
  if (event.key === 'ArrowLeft' && isNoteAtStart(event.target)) {
    if (!focusCandidates(region)) {
      paper?.focus();
    }
    return true;
  }
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    return stepColumn(event.key === 'ArrowUp' ? -1 : 1, event.target, region);
  }
  return false;
}

/** A döntés hasábjának állomásai, fentről lefelé — a DOM sorrendjében. */
const COLUMN =
  '.worklist__candidates input[type="radio"], .worklist__candidates-more, .booking__note';

function isNote(target: EventTarget): target is HTMLTextAreaElement {
  return target instanceof HTMLTextAreaElement && target.classList.contains('booking__note');
}

/**
 * `↑`/`↓` A DÖNTÉS HASÁBJÁBAN (Viktor, 10-20) — jelöltek → „További N
 * jelölt" → jegyzetek, egy sorban.
 *
 * A JELÖLTEN A NYÍL CSAK A FÓKUSZT VISZI, nem választ. A rádió saját nyila
 * választana is — és akkor az `Enter` egy véletlenül rálépett jelöltet
 * fűzne le; ugyanazért nincs előre bepipálva a legerősebb sem *(Viktor,
 * 09-23)*. A választás a `Space`-é és az egéré.
 *
 * A JEGYZETBEN A SZÉLSZABÁLY: a nyíl előbb a SZÖVEGBEN mozog (egy többsoros
 * jegyzet sorai közt), és csak a szöveg ELEJÉN (`↑`) vagy VÉGÉN (`↓`) lép.
 * Egysoros jegyzetben a böngésző első `↑`-ja is csak a szöveg elejére visz —
 * a második lép tovább. A kurzor az érkezés oldalán áll: felfelé a szöveg
 * VÉGÉN, lefelé az ELEJÉN.
 *
 * A HASÁB KÉT VÉGÉN a jelöltnél és a gombnál ELNYELJÜK a nyilat: különben a
 * munkalista ablak-szintű `↑`/`↓`-ja bizonylatot váltana a hasáb közepéből.
 * A jegyzet végén a nyíl a böngészőé marad.
 */
function stepColumn(direction: -1 | 1, target: EventTarget, region: HTMLElement | null): boolean {
  if (!(target instanceof HTMLElement && target.matches(COLUMN))) {
    return false;
  }
  if (isNote(target)) {
    const atEdge =
      direction === -1
        ? target.selectionStart === 0 && target.selectionEnd === 0
        : target.selectionStart === target.value.length &&
          target.selectionEnd === target.value.length;
    if (!atEdge) {
      return false;
    }
  }
  const stops = [...(region?.querySelectorAll<HTMLElement>(COLUMN) ?? [])];
  const next = stops[stops.indexOf(target) + direction];
  if (next === undefined) {
    return !isNote(target);
  }
  next.focus();
  if (isNote(next)) {
    const caret = direction === -1 ? next.value.length : 0;
    next.setSelectionRange(caret, caret);
  }
  return true;
}
